import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { FetchOptionsCallback } from 'global';
import { useNotifications } from 'modules/notification';

import { dealActivityApi } from './index';
import {
  DealActivityMutation,
  DealActivitiesMutation,
  CreateCallActivityVariables,
  UpdateCallActivityVariables,
  DeleteCallActivityVariables,
  DeleteCallActivitiesVariables,
} from './types';

export function useDealActivityMutations() {
  const { showNotification } = useNotifications();

  const [onCreate, { loading: isDealActivityCreating }] = useMutation<
    DealActivityMutation,
    CreateCallActivityVariables
  >(dealActivityApi.create);

  const handleCreateDealActivity = useCallback(
    async (dto: CreateCallActivityVariables['input'], fetchOptions?: FetchOptionsCallback<typeof dealActivityApi>) => {
      try {
        const { data } = await onCreate({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(dealActivityApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully created deal dealActivity');

        return data.dealActivity.result.entity!;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong creating deal', { type: 'error' });
      }
    },
    [onCreate, showNotification]
  );

  const [onUpdate, { loading: isDealActivityUpdating }] = useMutation<
    DealActivityMutation,
    UpdateCallActivityVariables
  >(dealActivityApi.update);

  const handleUpdateDealActivity = useCallback(
    async (dto: UpdateCallActivityVariables['input'], fetchOptions?: FetchOptionsCallback<typeof dealActivityApi>) => {
      try {
        const { data } = await onUpdate({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(dealActivityApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully updated deal dealActivity');

        return data.dealActivity.result.entity!;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong updating deal', { type: 'error' });
      }
    },
    [onUpdate, showNotification]
  );

  const [onDelete, { loading: isDealActivityDeleting }] = useMutation<
    DealActivityMutation,
    DeleteCallActivityVariables
  >(dealActivityApi.delete);

  const handleDeleteDealActivity = useCallback(
    async (dto: DeleteCallActivityVariables['input'], fetchOptions?: FetchOptionsCallback<typeof dealActivityApi>) => {
      try {
        const { data } = await onDelete({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(dealActivityApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully deleted deal dealActivity');

        return data.dealActivity.result.entity!;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong deleting deal', { type: 'error' });
      }
    },
    [onDelete, showNotification]
  );

  const [onDeleteMultiple, { loading: isDealActivityDeletingMultiple }] = useMutation<
    DealActivitiesMutation,
    DeleteCallActivitiesVariables
  >(dealActivityApi.deleteMultiple, {});

  const handleDeleteMultipleDealActivities = useCallback(
    async (
      dto: DeleteCallActivitiesVariables['input'],
      fetchOptions?: FetchOptionsCallback<typeof dealActivityApi>
    ) => {
      try {
        const { data } = await onDeleteMultiple({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(dealActivityApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully deleted deal activities');

        return data.dealActivity.result.list!;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong deleting deal activities', { type: 'error' });
      }
    },
    [onDeleteMultiple, showNotification]
  );

  return {
    handleCreateDealActivity,
    handleUpdateDealActivity,
    handleDeleteDealActivity,
    handleDeleteMultipleDealActivities,
    isDealActivityCreating,
    isDealActivityUpdating,
    isDealActivityDeleting,
    isDealActivityDeletingMultiple,
  };
}
